import { __extends } from "tslib";
import { PoissonLookup } from '@awayjs/core';
import { ShadowMethodBase, _Shader_ShadowMethodBase } from './ShadowMethodBase';
/**
 * ShadowSoftMethod provides a soft shadowing technique by randomly distributing
 * sample points.
 */
var ShadowSoftMethod = /** @class */ (function (_super) {
    __extends(ShadowSoftMethod, _super);
    /**
     * Creates a new DiffuseBasicMethod object.
     *
     * @param castingLight The light casting the shadows
     * @param numSamples The amount of samples to take for dithering. Minimum 1,
     * maximum 32.
     */
    function ShadowSoftMethod(castingLight, numSamples, range) {
        if (numSamples === void 0) { numSamples = 5; }
        if (range === void 0) { range = 1; }
        var _this = _super.call(this, castingLight) || this;
        _this.numSamples = numSamples;
        _this.range = range;
        return _this;
    }
    Object.defineProperty(ShadowSoftMethod.prototype, "assetType", {
        /**
         * @inheritDoc
         */
        get: function () {
            return ShadowSoftMethod.assetType;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ShadowSoftMethod.prototype, "offsets", {
        get: function () {
            return this._offsets;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ShadowSoftMethod.prototype, "numSamples", {
        /**
         * The amount of samples to take for dithering. Minimum 1, maximum 32. The
         * actual maximum may depend on the complexity of the shader.
         */
        get: function () {
            return this._numSamples;
        },
        set: function (value) {
            this._numSamples = value;
            if (this._numSamples < 1)
                this._numSamples = 1;
            else if (this._numSamples > 32)
                this._numSamples = 32;
            this._offsets = PoissonLookup.getDistribution(this._numSamples);
            this.invalidateShaderProgram();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ShadowSoftMethod.prototype, "range", {
        /**
         * The range in the shadow map in which to distribute the samples.
         */
        get: function () {
            return this._range;
        },
        set: function (value) {
            this._range = value;
        },
        enumerable: true,
        configurable: true
    });
    ShadowSoftMethod.assetType = '[asset ShadowSoftMethod]';
    return ShadowSoftMethod;
}(ShadowMethodBase));
export { ShadowSoftMethod };
import { ShaderBase } from '@awayjs/renderer';
/**
 * _Shader_ShadowSoftMethod provides a soft shadowing technique by randomly
 * distributing sample points.
 */
var _Shader_ShadowSoftMethod = /** @class */ (function (_super) {
    __extends(_Shader_ShadowSoftMethod, _super);
    /**
     * Creates a new ShadowHardChunk.
     */
    function _Shader_ShadowSoftMethod(method, shader) {
        return _super.call(this, method, shader) || this;
    }
    /**
     * @inheritDoc
     */
    _Shader_ShadowSoftMethod.prototype._initConstants = function () {
        _super.prototype._initConstants.call(this);
        this._shader.fragmentConstantData[this._fragmentConstantsIndex] = 1 / this._method.numSamples;
        this._shader.fragmentConstantData[this._fragmentConstantsIndex + 1] = 0;
    };
    /**
     * @inheritDoc
     */
    _Shader_ShadowSoftMethod.prototype._activate = function () {
        _super.prototype._activate.call(this);
        var texRange = .5 * this._method.range / this._method.castingLight.shadowMapper.size;
        var data = this._shader.fragmentConstantData;
        var index = this._fragmentConstantsIndex + 2;
        var len = this._method.numSamples << 1;
        for (var i = 0; i < len; ++i)
            data[index + i] = this._method.offsets[i] * texRange;
    };
    /**
     * @inheritDoc
     */
    _Shader_ShadowSoftMethod.prototype._getFragmentCode = function (targetReg, registerCache, sharedRegisters) {
        var dataReg = registerCache.getFreeFragmentConstant();
        this._fragmentConstantsIndex = dataReg.index * 4;
        var code = '';
        var uvReg = registerCache.getFreeFragmentVectorTemp();
        registerCache.addFragmentTempUsages(uvReg, 1);
        var offsets = new Array(dataReg + '.zw');
        var numRegs = this._method.numSamples >> 1;
        for (var i = 0; i < numRegs; ++i) {
            var reg = registerCache.getFreeFragmentConstant();
            offsets.push(reg + '.xy');
            offsets.push(reg + '.zw');
        }
        var temp = registerCache.getFreeFragmentVectorTemp();
        for (i = 0; i < this._method.numSamples; ++i) {
            if (i == 0) {
                code += 'add ' + uvReg + ', ' + this._baseChunk.depthMapCoordReg + ', ' + dataReg + '.zwyy\n' +
                    this._baseTexture._getFragmentCode(targetReg, registerCache, sharedRegisters, uvReg);
            }
            else {
                code += 'add ' + uvReg + '.xy, ' + this._baseChunk.depthMapCoordReg + '.xy, ' + offsets[i] + '\n' +
                    this._baseTexture._getFragmentCode(temp, registerCache, sharedRegisters, uvReg) +
                    'add ' + targetReg + '.w, ' + targetReg + '.w, ' + temp + '.w\n';
            }
        }
        registerCache.removeFragmentTempUsage(uvReg);
        code += 'mul ' + targetReg + '.w, ' + targetReg + '.w, ' + dataReg + '.x\n' + // average
            _super.prototype._getFragmentCode.call(this, targetReg, registerCache, sharedRegisters);
        return code;
    };
    return _Shader_ShadowSoftMethod;
}(_Shader_ShadowMethodBase));
export { _Shader_ShadowSoftMethod };
ShaderBase.registerAbstraction(_Shader_ShadowSoftMethod, ShadowSoftMethod);
